import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import Auth from "../../utils/AuthUtils";
import ProjectProgressApi from "../../api/ProjectProgressApi";
import { showError, showSuccess } from "../../utils/toastUtils";
import { can } from "../../utils/permissionUtils";

function ProjectProgress() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [updateData, setUpdateData] = useState({ stage: "", status: "", percentage: 0 });
  const [updating, setUpdating] = useState(false);

  const user = Auth.getUserDetails();
  const permissions = user?.permissions;

  // ✅ 1. Status wise Color Logic
  const getStageBadge = (stage) => {
    switch (stage?.toLowerCase()) {
      case "planning": return "bg-soft-info text-info";
      case "development": return "bg-soft-warning text-warning";
      case "testing": return "bg-soft-secondary text-dark";
      case "live": return "bg-soft-success text-success";
      default: return "bg-light text-muted";
    }
  };

  /* ================= FETCH DATA ================= */
  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      const res = await ProjectProgressApi.list();
      if (res.code === 200) {
        setProjects(res.data);
        setFilteredProjects(res.data);
      }
    } catch (err) {
      showError("Failed to load project data");
    } finally {
      setLoading(false);
    }
  }, []);

  /* ================= INITIAL LOAD ================= */
  useEffect(() => {
    const hasAccess = can(permissions, "read", "project");
    if (hasAccess) {
      loadProjects();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); 

  /* ================= SEARCH FILTER ================= */
  useEffect(() => {
    const filtered = projects.filter(p => 
      p.templateId?.templateName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.clientId?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredProjects(filtered);
  }, [searchTerm, projects]);

  /* ================= UPDATE LOGIC ================= */
  const handleUpdateStage = async (e) => {
    e.preventDefault();
    try {
      setUpdating(true);
      const res = await ProjectProgressApi.updateStage({
        projectId: selectedProject._id,
        moduleId: selectedModule._id,
        ...updateData
      });

      if (res.code === 200) {
        showSuccess("Milestone updated!");
        loadProjects();
        const modalElement = document.getElementById("updateStageModal");
        const modalInstance = window.bootstrap.Modal.getInstance(modalElement);
        modalInstance?.hide();
      }
    } catch (err) {
      showError("Update failed");
    } finally {
      setUpdating(false);
    }
  };

  const openUpdateModal = (project, module) => {
    setSelectedProject(project);
    setSelectedModule(module);
    setUpdateData({
      stage: module.stage,
      status: module.status,
      percentage: module.completionPercentage
    });
  };

  if (!can(permissions, "read", "project")) return <Navigate to="/403" replace />;

  return (
    <div className="content pb-0">
      {/* Header Section */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold mb-1">Project Progress Tracking</h4>
          <p className="text-muted small mb-0">Monitor milestones and stages.</p>
        </div>
        <div className="d-flex gap-2">
          <div className="input-group input-group-sm" style={{ width: '250px' }}>
            <span className="input-group-text bg-white border-end-0"><i className="ti ti-search"></i></span>
            <input 
              type="text" 
              className="form-control border-start-0" 
              placeholder="Search project..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="btn btn-light btn-sm border" onClick={() => navigate("/dashboard")}>Back</button>
        </div>
      </div>

      {loading ? (
        <div className="text-center p-5"><div className="spinner-border text-primary"></div></div>
      ) : filteredProjects.length === 0 ? (
        <div className="card text-center p-5 border-0 shadow-sm">
          <i className="ti ti-folder-off fs-1 text-muted mb-2"></i>
          <h5 className="text-muted">No matching projects found.</h5>
        </div>
      ) : (
        <div className="row">
          {filteredProjects.map((proj) => (
            <div className="col-12 mb-4" key={proj._id}>
              <div className="card border-0 shadow-sm overflow-hidden">
                <div className="card-header bg-white py-3 border-bottom d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="mb-1 fw-bold text-dark">{proj.templateId?.templateName || "Web Project"}</h6>
                    <span className="small text-muted"><i className="ti ti-user me-1"></i>Client: {proj.clientId?.name}</span>
                  </div>
                  <div style={{ width: "220px" }}>
                    <div className="d-flex justify-content-between mb-1">
                      <small className="fw-bold text-muted">Overall Progress</small>
                      <small className="fw-bold text-success">{proj.overallProgress}%</small>
                    </div>
                    <div className="progress" style={{ height: "8px", borderRadius: '10px' }}>
                      <div className={`progress-bar ${proj.overallProgress === 100 ? 'bg-success' : 'bg-primary'}`} style={{ width: `${proj.overallProgress}%` }}></div>
                    </div>
                  </div>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="ps-4">Milestone</th>
                          <th>Stage</th>
                          <th>Status</th>
                          <th>Progress</th>
                          {can(permissions, "write", "project") && <th className="text-end pe-4">Action</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {proj.modules.map((mod) => (
                          <tr key={mod._id}>
                            <td className="ps-4 fw-semibold">
                              {/* ✅ mod.moduleReference.module_name use karein kyunki controller mein yahi hai */}
                              {mod.moduleReference?.module_name || "Untitled Milestone"}
                            </td>
                            <td><span className={`badge border-0 ${getStageBadge(mod.stage)}`}>{mod.stage}</span></td>
                            <td>
                              <span className={`badge rounded-pill ${mod.status === 'Completed' ? 'bg-success' : 'bg-warning'}`}>
                                {mod.status}
                              </span>
                            </td>
                            <td style={{ width: "200px" }}>
                                <div className="d-flex align-items-center gap-2">
                                    <div className="progress flex-grow-1" style={{ height: "4px" }}>
                                        <div className="progress-bar" style={{ width: `${mod.completionPercentage}%` }}></div>
                                    </div>
                                    <small className="fw-bold text-muted">{mod.completionPercentage}%</small>
                                </div>
                            </td>
                            {can(permissions, "write", "project") && (
                              <td className="text-end pe-4">
                                <button className="btn btn-xs btn-outline-primary" data-bs-toggle="modal" data-bs-target="#updateStageModal" onClick={() => openUpdateModal(proj, mod)}>
                                  <i className="ti ti-edit"></i>
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* UPDATE MODAL */}
      <div className="modal fade" id="updateStageModal" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered modal-sm">
          <div className="modal-content border-0 shadow-lg">
            <div className="modal-header bg-primary text-white py-2">
              <h6 className="modal-title fw-bold text-white">Update Milestone</h6>
              <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal"></button>
            </div>
            <form onSubmit={handleUpdateStage}>
              <div className="modal-body p-4">
                <div className="mb-3">
                  <label className="form-label small fw-bold">Project Stage</label>
                  <select className="form-select form-select-sm" value={updateData.stage} onChange={(e) => setUpdateData({...updateData, stage: e.target.value})}>
                    <option value="Planning">Planning</option>
                    <option value="Development">Development</option>
                    <option value="Testing">Testing</option>
                    <option value="Live">Live</option>
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label small fw-bold">Status</label>
                  <select className="form-select form-select-sm" value={updateData.status} onChange={(e) => setUpdateData({...updateData, status: e.target.value})}>
                    <option value="Pending">Pending</option>
                    <option value="In-Progress">In-Progress</option>
                    <option value="Completed">Completed</option>
                  </select>
                </div>
                <div className="mb-0">
                  <label className="form-label small fw-bold d-flex justify-content-between">Progress <span>{updateData.percentage}%</span></label>
                  <input type="range" className="form-range" min="0" max="100" value={updateData.percentage} onChange={(e) => setUpdateData({...updateData, percentage: parseInt(e.target.value)})} />
                </div>
              </div>
              <div className="modal-footer pt-0 border-0">
                <button type="submit" className="btn btn-primary btn-sm w-100 py-2" disabled={updating}>
                    {updating ? "Saving..." : "Confirm Update"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProjectProgress;